import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import vehiclePlaceholder from 'assets/images/vehicle-placeholder.svg';
import { ReactComponent as Vtprestige } from 'assets/images/prestige.svg';
import { IVehicleSummary } from 'common/types';
import { IMAGE_URL } from 'common/constants';
import { formatDate } from 'common/helpers';
import { selectUser } from 'modules/me/slice';
import useAppSelector from 'hooks/useAppSelector';

interface MyComponentProps {
  vehicle: IVehicleSummary;
}

interface Props {
  vehicleSummary: IVehicleSummary | null;
  startDate: Date;
  endDate: Date;
}

const BookingInfo = ({ vehicleSummary, startDate, endDate }: Props) => {
  const getFuel = () => vehicleSummary?.fuelType.replaceAll('_', ' ');
  const getType = () => vehicleSummary?.group.category?.name;
  const getFuelPin = () => vehicleSummary?.fuelCardPincode;
  const getVehicleCapacity = () => vehicleSummary?.seats;

  const user = useAppSelector(selectUser);
  const premiumFaqUrl = window.location.hostname.includes('flexicar.com.au')
    ? 'https://www.flexicar.com.au/faq/category?cid=2&ref=content-15'
    : 'https://web-nz.flexicar.apse2.hertz.io/faq/category?cid=10&ref=content-172';
  const [showPremiumTooltip, setShowPremiumTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setShowPremiumTooltip(false);
      }
    };
    if (showPremiumTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPremiumTooltip]);

  const VehicleImage: React.FC<MyComponentProps> = ({ vehicle }) => {
    const [vehicleImageError, setVehicleImageError] = React.useState(false);
    const [groupImageError, setGroupImageError] = React.useState(false);

    return (
      <div className="ml-auto h-[170px] w-[180px] md:w-[255px]">
        <img
          className={classNames('hidden h-full w-full object-contain', {
            '!block': !vehicleImageError,
          })}
          src={vehicle.vehicleImageUrl !== null ? vehicle.vehicleImageUrl : ''}
          alt={vehicle.name}
          onError={() => setVehicleImageError(true)}
        />
        <img
          className={classNames('hidden h-full w-full object-contain', {
            '!block': vehicleImageError && !groupImageError,
          })}
          src={
            vehicle.group.vehicleImage !== null
              ? vehicle.group.vehicleImage
              : ''
          }
          alt={vehicle.group.make}
          onError={() => setGroupImageError(true)}
        />
        <img
          className={classNames('hidden h-full w-full object-contain', {
            '!block': vehicleImageError && groupImageError,
          })}
          src={`${IMAGE_URL}${vehiclePlaceholder}`}
          alt="vehicle default image"
        />
      </div>
    );
  };

  return (
    <section className="flex w-full flex-col items-start">
      <div className="mb-[30px] flex w-full justify-between  ">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col">
            <div className="flex items-center gap-[10px]">
              {vehicleSummary?.isPrestige && (
                <Vtprestige className="h-[20px] w-[20px]" />
              )}
              <p className="mr-[20px]  text-left text-[22px] font-bold sm:text-[26px]">
                {vehicleSummary?.name}
              </p>
            </div>
            <p className="background-red flex items-center gap-2 whitespace-nowrap fill-zinc-700 text-[16px] sm:text-[16px]">
              {vehicleSummary?.group.make}, {vehicleSummary?.group.model}
            </p>

            <p className="background-red flex items-center gap-2 whitespace-nowrap fill-zinc-700 text-[16px] sm:text-[16px]">
              {vehicleSummary?.group.model}
            </p>
          </div>
        </div>

        <div className="h-full w-full object-contain">
          {vehicleSummary && <VehicleImage vehicle={vehicleSummary} />}
        </div>
      </div>
      <div className="mb-[50px] w-full">
        <p className="mb-[30px] border-b-[1px] border-[#282828] pb-[10px] text-[20px] sm:text-[22px]">
          Car information
        </p>

        <div className=" grid grid-cols-2 gap-y-[20px] text-[16px] sm:text-[16px] [&>p]:border-b-[1px] [&>p]:border-dotted">
          <p>Number of passengers</p>
          <p className="text-end font-bold capitalize">
            {getVehicleCapacity()}
          </p>
          <p>Fuel</p>
          <p className="text-end font-bold capitalize">{getFuel()}</p>
          <p>Type</p>
          <p className="text-end font-bold capitalize">{getType()}</p>
          <p>Fuel Pin</p>
          <p className="text-end font-bold capitalize">{getFuelPin()}</p>
        </div>
      </div>

      <div
        className={classNames('flex w-full flex-col gap-5', {
          'border-b-[1px] border-[#282828] pb-[50px]':
            user?.plan?.name !== 'business',
        })}
      >
        <div className="flex flex-col">
          <p className="mb-[30px] border-b-[1px] border-[#282828] pb-[10px] text-[20px] sm:text-[22px]">
            Booking Details
          </p>
          <div className="grid grid-cols-2 gap-y-[20px] text-[16px] sm:text-[16px] [&>p]:border-b-[1px] [&>p]:border-dotted">
            <p>Pick-up</p>
            <p className="text-end font-bold">
              {formatDate(startDate.toISOString(), 'MMMM DD | h:mm A')}
            </p>
            <p>Drop-off</p>
            <p className="text-end font-bold">
              {formatDate(endDate.toISOString(), 'MMMM DD | h:mm A')}
            </p>
            <p className="border-b-transparent">Car location</p>
            {vehicleSummary?.podIsPremium ? (
              <div
                className="relative flex justify-end border-b-transparent"
                ref={tooltipRef}
                onMouseEnter={() => setShowPremiumTooltip(true)}
                onMouseLeave={() => setShowPremiumTooltip(false)}
              >
                <span
                  className="shrink-0 cursor-pointer rounded-[24px] bg-[#98e6dc] px-[6px] py-[2px] text-[10px] font-bold leading-[1.2] text-[#18665c] self-center"
                  onClick={() =>
                    setShowPremiumTooltip(!showPremiumTooltip)
                  }
                >
                  Premium Location
                </span>
                {showPremiumTooltip && (
                  <div className="absolute right-0 top-full z-10 mt-[8px] flex w-[250px] flex-col items-center">
                    <div className="absolute right-[20px] top-[-6px] h-[12px] w-[12px] rotate-45 bg-white shadow-[-2px_-2px_4px_rgba(0,0,0,0.04)]" />
                    <div className="flex flex-col gap-[12px] rounded-[8px] bg-white p-[14px] shadow-[0px_2px_12px_0px_rgba(0,0,0,0.04),0px_4px_20px_0px_rgba(0,0,0,0.08)]">
                      <p className="text-[14px] font-bold leading-[1.2] tracking-[0.2px] text-[#282828]">
                        Premium Location
                      </p>
                      <p className="text-[12px] leading-[18px] tracking-[0.2px] text-[#787878]">
                        This car is located in a premium location (such as
                        a commercial car park), so a location surcharge
                        applies.
                      </p>
                      <a href={premiumFaqUrl} className="block text-right text-[12px] font-bold leading-none text-[#ff7800]">
                        Learn more
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-end font-bold border-b-transparent">
                {vehicleSummary?.podName}
              </p>
            )}
            {vehicleSummary?.podIsPremium && (
              <p className="col-span-2 font-bold border-b-transparent">
                {vehicleSummary?.podName}
              </p>
            )}
            <p className="col-span-2 border-b-transparent text-[14px] sm:text-[14px]">
              {vehicleSummary?.podDescription}
            </p>
          </div>

          {vehicleSummary?.breakdowns.includedKilometers &&
            vehicleSummary.breakdowns.includedKilometers.rate > 0 && (
            <>
              <div className="mt-[20px] grid grid-cols-2 gap-y-[20px] text-[16px] sm:text-[16px] [&>p]:border-b-[1px] [&>p]:border-dotted">
                <p>Included kilometers</p>
                <p className="text-end font-bold">
                  {vehicleSummary.breakdowns.includedKilometers.amount} km
                </p>
              </div>
              <p className="mt-[10px] text-[12px] italic text-[#282828]">
                {vehicleSummary.breakdowns.includedKilometers.description}
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default BookingInfo;
